import type { KeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Pill, SurfacePagePanel } from '@/design-system';
import { cn } from '@/shared/cn';
import type { CapacityStrategyId, CapacityStrategyRead, MetalPriceState } from '@/versions/v1/schema/capacity';
import { STRATEGY_APPROVAL_REVIEW_ID } from '@/versions/v1/shared/capacity-seed';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { CapacityRange } from './CapacityRange';
import {
  strategyTabProjection,
  type AgLineView,
  type MoneyTone,
  type StrategyMatrixBar,
  type StrategyMatrixCellView,
  type StrategyTabProjection,
} from './StrategyTab.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** The larger circuit's per-ppt value is the reference the other is read against. */
const REFERENCE_CAPTION = '100% reference';

/** Circuit identity follows the app's circuit tokens (Ag rides the Pb/Ag circuit). */
const CIRCUIT_INK = {
  zinc: 'color-mix(in srgb, var(--circuit-zn) 78%, var(--ink-primary))',
  'lead-silver': 'color-mix(in srgb, var(--circuit-pb) 78%, var(--ink-primary))',
} as const;

const MONEY_TEXT: Record<MoneyTone | 'warn', string> = {
  ok: 'text-[var(--state-online)]',
  bad: 'text-[var(--state-blocked)]',
  warn: 'text-[var(--state-warning)]',
  neutral: 'text-muted',
};

const SELECTED_CELL = 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]';
const CELL = 'border-b border-[var(--color-divider)] px-2.5 py-[7px] text-right align-middle';

export function StrategyTab({
  projectId,
  prices,
  deck,
  read,
  agEnds,
  feedTag,
  selectedId,
  onPricesChange,
  onStrategyChange,
}: {
  projectId: string;
  prices: MetalPriceState;
  deck: MetalPriceState;
  read: CapacityStrategyRead;
  agEnds: { low: CapacityStrategyRead; high: CapacityStrategyRead };
  feedTag: string;
  selectedId: CapacityStrategyId;
  onPricesChange: (next: MetalPriceState) => void;
  onStrategyChange: (id: CapacityStrategyId) => void;
}) {
  const advisory = useAdvisory();
  const approved = advisory.approvedChangeIds.has(STRATEGY_APPROVAL_REVIEW_ID);
  const view = strategyTabProjection(read, selectedId, approved, { deck, agEnds, feedTag });
  const approvalHref = `/projects/${projectId}/advisory?tab=approvals&review=${STRATEGY_APPROVAL_REVIEW_ID}`;
  const [zinc, lead, silver] = view.priceControls;
  const captions = view.circuitCaptions;
  const priceRange = (control: typeof zinc) => (
    <CapacityRange
      label={`${control!.label} · ${control!.unit}`}
      inputLabel={`${control!.label} price`}
      display={control!.display}
      min={control!.min}
      max={control!.max}
      step={control!.step}
      value={control!.value}
      minLabel={control!.minLabel}
      maxLabel={control!.maxLabel}
      fillPct={control!.fillPct}
      moved={control!.moved}
      markPct={control!.markPct}
      markLabel={control!.markLabel}
      onChange={(value) => onPricesChange({ ...prices, [control!.key]: value })}
    />
  );

  return (
    <div data-strategy-tab className="space-y-3">
      {/* K18 r1: the price-slider columns take their width back from the row gap
          and a slightly smaller silver/captions share, so `Zinc (Zn) · US$/dmt`
          and `Lead (Pb) · US$/dmt` show whole at 1470 instead of truncating.
          F16: below 1400 the five tracks leave ~72px for a price label and the
          1.31fr captions track wraps `PB/AG CIRCUIT`; the card reflows to a
          2-up grid (label row, then two controls per row) so every label reads
          whole. The ≥1400 tracks are untouched, so 1470 renders as before. */}
      <section
        id="walkthrough-capacity-strategy"
        aria-label="Metal prices"
        className="card-matte-2 grid grid-cols-2 items-center gap-4 rounded-2xl px-4 py-3.5 min-[1400px]:grid-cols-[auto_minmax(0,0.945fr)_minmax(0,0.945fr)_minmax(0,1.95fr)_minmax(0,1.31fr)]"
      >
        <div className="col-span-2 flex flex-col gap-0.5 min-[1400px]:col-span-1">
          <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Metal prices</span>
          <span className="whitespace-nowrap text-xs tabular-nums text-muted">{view.priceDateLabel}</span>
        </div>
        {priceRange(zinc)}
        {priceRange(lead)}
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_150px] items-center gap-3.5">
          {priceRange(silver)}
          {view.agLine ? <AgLine line={view.agLine} /> : null}
        </div>
        <div className="grid grid-cols-2 gap-3.5 tabular-nums lg:border-l lg:border-[var(--color-divider)] lg:pl-4">
          {/* K18 r2: below 1400 the unit drops under its figure, so a
              `US$1,584` figure never pushes `/day` past the card edge at 1280. */}
          <div className="min-w-0">
            <span className="text-xs font-semibold uppercase tracking-[0.07em]" style={{ color: CIRCUIT_INK['lead-silver'] }}>Pb/Ag circuit</span>
            <div className="text-lg font-semibold text-ink-primary">
              {captions.leadSilverFigure}<span className="block text-xs font-normal text-muted min-[1400px]:inline">/day per pt</span>
            </div>
            <div className="mb-1 mt-1.5 h-1.5 rounded bg-[var(--hairline-strong)]">
              <div className="h-full rounded bg-[var(--circuit-pb)]" style={{ width: `${captions.leadSilverBarPct}%` }} />
            </div>
            <div className="text-xs text-muted">{captions.leadSilver}{captions.leadSilverIsReference ? ` · ${REFERENCE_CAPTION}` : ''}</div>
          </div>
          <div className="min-w-0">
            <span className="text-xs font-semibold uppercase tracking-[0.07em]" style={{ color: CIRCUIT_INK.zinc }}>Zn circuit</span>
            <div className="text-lg font-semibold text-ink-primary">
              {captions.zincFigure}<span className="block text-xs font-normal text-muted min-[1400px]:inline">/day per pt</span>
            </div>
            <div className="mb-1 mt-1.5 h-1.5 rounded bg-[var(--hairline-strong)]">
              <div className="h-full rounded bg-[var(--circuit-zn)]" style={{ width: `${captions.zincBarPct}%` }} />
            </div>
            <div className="text-xs text-muted">{captions.zincIsReference ? REFERENCE_CAPTION : captions.zinc}</div>
          </div>
        </div>
      </section>

      {/* F16: below 1400 the 9-column table is narrower than its 720px minimum
          and the Financial impact card hides the fourth strategy column, so the
          two stack (P19/P21 pattern); ≥1400 keeps the 9/3 split. */}
      <div className="grid grid-cols-1 items-start gap-3 min-[1400px]:grid-cols-12">
        <section aria-label="Strategy comparison" data-strategy-table-wrap className="card-matte-2 col-span-12 min-w-0 overflow-x-auto rounded-2xl px-2 pb-2.5 pt-1.5 min-[1400px]:col-span-9">
          <StrategyMatrix
            view={view}
            recommendation={<Pill tone="success" className="text-[10px] font-semibold tracking-[0.05em]">AI recommends: {view.recommendation.shortName}</Pill>}
            approvalHref={approvalHref}
            onSelect={onStrategyChange}
          />
        </section>

        <SurfacePagePanel title="Financial impact" className="col-span-12 min-w-0 min-[1400px]:col-span-3" contentClassName="space-y-2.5">
          <div className="grid grid-cols-2 gap-2 tabular-nums">
            <div className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-2)] px-2.5 py-2">
              <span className="text-xs leading-snug text-muted">{view.financial.currentLabel}</span>
              <b className="mt-0.5 block text-lg font-semibold text-ink-primary">{view.financial.currentValue}</b>
            </div>
            <div className="rounded-xl border border-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] px-2.5 py-2">
              <span className="text-xs leading-snug text-muted">{view.financial.selectedLabel}</span>
              <b className="mt-0.5 block text-lg font-semibold text-ink-primary">{view.financial.selectedValue}</b>
            </div>
          </div>
          {/* K18 r1: px-2.5 keeps `Improvement vs current · US$/day` on one line
              at 1280. */}
          <div className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-2)] px-2.5 py-2.5 tabular-nums">
            <span className="text-xs text-muted">Improvement vs current · {CURRENCY_GLYPH}/day</span>
            <div className={cn('text-2xl font-semibold leading-tight tracking-[-0.01em]', view.financial.deltaTone === 'neutral' ? 'text-ink-primary' : MONEY_TEXT[view.financial.deltaTone])}>
              {view.financial.deltaValue}
            </div>
            <span className="text-xs text-muted">{view.financial.annualised}</span>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Strategy EBITDA · {CURRENCY_GLYPH}/day</div>
            <div className="mt-1 tabular-nums">
              {view.comparison.map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-1 rounded-lg px-2 py-1.5 text-xs text-ink-primary even:bg-[var(--surface-1)]">
                  {/* K18 r1: gap-1 keeps `Zn recovery focus` on one line at 1280
                      beside the wider US$ value. */}
                  <span className="min-w-0">{row.label}</span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <b className="font-semibold">{row.ebitdaLabel}</b>
                    {row.best ? <Pill tone="success" className="px-1.5 py-0 text-[10px] font-semibold tracking-[0.05em]">Best</Pill> : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <p className="border-t border-[var(--color-divider)] pt-2 text-xs tabular-nums text-muted">{view.breakevenLabel}</p>
        </SurfacePagePanel>
      </div>
    </div>
  );
}

function MatrixBar({ bar }: { bar: StrategyMatrixBar }) {
  return (
    <span className="relative mb-1 mt-1.5 block h-2 rounded-[3px] bg-[var(--hairline)]" aria-hidden>
      <i className="absolute inset-y-0 left-0 rounded-l-[3px] bg-[var(--hairline-strong)]" style={{ width: `${bar.basePct}%` }} />
      {bar.deltaWidthPct > 0 ? (
        <em
          className={cn('absolute inset-y-0 rounded-r-[3px] opacity-85', bar.deltaTone === 'bad' ? 'bg-[var(--state-blocked)]' : 'bg-[var(--state-online)]')}
          style={{ left: `${bar.deltaLeftPct}%`, width: `${bar.deltaWidthPct}%` }}
        />
      ) : null}
    </span>
  );
}

function MatrixCell({ cell, selected }: { cell: StrategyMatrixCellView; selected: boolean }) {
  return (
    <td className={cn(CELL, selected && SELECTED_CELL, cell.unchanged && 'text-muted')}>
      {cell.unchanged ? cell.value : (
        <>
          <b className={cn('whitespace-nowrap font-semibold', cell.valueTone ? MONEY_TEXT[cell.valueTone] : 'text-ink-primary')}>{cell.value}</b>
          {cell.impact ? (
            <span className={cn('block text-xs', cell.impactTone === 'neutral' ? 'font-medium leading-snug text-muted' : cn('whitespace-nowrap font-semibold', MONEY_TEXT[cell.impactTone ?? 'ok']))}>
              {cell.impact}
            </span>
          ) : null}
        </>
      )}
    </td>
  );
}

function StrategyMatrix({
  view,
  recommendation,
  approvalHref,
  onSelect,
}: {
  view: StrategyTabProjection;
  recommendation: ReactNode;
  approvalHref: string;
  onSelect: (id: CapacityStrategyId) => void;
}) {
  const matrix = view.matrixView;
  const selectOnKey = (id: CapacityStrategyId) => (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(id);
    }
  };
  return (
    <table className="w-full min-w-[720px] table-fixed border-separate border-spacing-0 text-sm tabular-nums" aria-label="Strategy comparison">
      <thead>
        <tr>
          <th scope="col" className="w-[200px] border-b border-[var(--hairline-strong)] px-2.5 pb-[7px] pt-2.5 text-left align-bottom font-normal">
            <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Strategy</span>
            <div className="mt-1.5">{recommendation}</div>
            <span className="mt-2 block text-xs text-muted">{matrix.scaleLabel}</span>
          </th>
          <th scope="col" className="border-b border-[var(--hairline-strong)] px-2.5 pb-[7px] pt-2.5 text-right align-bottom font-normal">
            <span className="block text-sm font-semibold text-ink-primary">{matrix.current.name}</span>
            <span className="mt-0.5 block text-xl font-semibold text-ink-primary">{matrix.current.ebitda}</span>
            <MatrixBar bar={matrix.current.bar} />
            <span className="block text-xs font-medium text-muted">{matrix.current.caption}</span>
          </th>
          {matrix.columns.map((column) => (
            <th
              key={column.id}
              scope="col"
              role="button"
              tabIndex={0}
              aria-pressed={column.selected}
              onClick={() => onSelect(column.id)}
              onKeyDown={selectOnKey(column.id)}
              className={cn(
                'cursor-pointer border-b border-[var(--hairline-strong)] px-2.5 pb-[7px] pt-2.5 text-right align-bottom font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]',
                column.selected
                  ? `${SELECTED_CELL} shadow-[inset_0_3px_0_var(--color-accent-500)]`
                  : 'hover:bg-[var(--surface-1)]',
              )}
            >
              <span className="block text-sm font-semibold leading-snug text-ink-primary">
                {column.name}
                {column.draft ? <Pill tone="warning" className="ml-1.5 px-1.5 py-0 align-[1px] text-[10px] font-semibold tracking-[0.05em]">Draft</Pill> : null}
                {column.best ? <Pill tone="success" className="ml-1.5 px-1.5 py-0 align-[1px] text-[10px] font-semibold tracking-[0.05em]">Best</Pill> : null}
              </span>
              <span className="mt-0.5 block text-xl font-semibold text-ink-primary">{column.ebitda}</span>
              <MatrixBar bar={column.bar} />
              <span className={cn('block text-xs font-semibold', MONEY_TEXT[column.upliftTone])}>{column.uplift}</span>
              <span className="block text-xs font-medium text-muted">{column.annualised}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={cn(CELL, 'text-left font-medium text-ink-primary')}>Feed · recovery</td>
          <td className={CELL}>
            <span className="text-ink-secondary">{matrix.current.feed}</span>
            <span className="block text-xs text-muted">{matrix.current.recoveries}</span>
          </td>
          {matrix.columns.map((column) => (
            <td key={column.id} className={cn(CELL, column.selected && SELECTED_CELL)}>
              <b className="font-semibold text-ink-primary">{column.feed}</b>
              <span className="block text-xs text-muted">{column.recoveries}</span>
            </td>
          ))}
        </tr>
        {matrix.groups.map((group) => (
          <MatrixGroup key={group.id} group={group} columns={matrix.columns} />
        ))}
        <tr>
          <td className={cn(CELL, 'text-left font-medium text-ink-primary')}>Net vs Balanced</td>
          <td className={CELL} />
          {matrix.columns.map((column) => (
            <MatrixCell key={column.id} cell={matrix.netVsBalanced[column.id]} selected={column.selected} />
          ))}
        </tr>
        {matrix.approval ? (
          <tr>
            <td colSpan={5} className="px-2.5 pt-2 text-left">
              <span className="text-xs text-muted">{matrix.approval} · </span>
              <Link className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-accent-600)] hover:underline dark:text-[var(--color-accent-300)]" to={approvalHref}>
                Review in Advisory › Approvals <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

function MatrixGroup({
  group,
  columns,
}: {
  group: StrategyTabProjection['matrixView']['groups'][number];
  columns: StrategyTabProjection['matrixView']['columns'];
}) {
  const ink = group.id === 'lead-silver' || group.id === 'zinc' ? CIRCUIT_INK[group.id] : undefined;
  return (
    <>
      <tr>
        <td colSpan={5} className={cn(CELL, 'pt-3 text-left text-xs font-semibold uppercase tracking-[0.07em]', !ink && 'text-muted')} style={ink ? { color: ink } : undefined}>
          {group.label}
        </td>
      </tr>
      {group.rows.map((row) => (
        <tr key={row.id}>
          <td className={cn(CELL, 'text-left')}>
            <span className="font-medium text-ink-primary">{row.label}</span>
            <span className="block max-w-[34ch] text-xs leading-snug text-muted">{row.driver}</span>
          </td>
          <td className={cn(CELL, 'text-muted')}>{row.current}</td>
          {columns.map((column) => (
            <MatrixCell key={column.id} cell={row.cells[column.id]} selected={column.selected} />
          ))}
        </tr>
      ))}
    </>
  );
}

function AgLine({ line }: { line: AgLineView }) {
  return (
    <div className="flex w-[150px] flex-col items-start gap-0.5">
      <svg viewBox={`0 0 ${line.width} ${line.height}`} width={line.width} height={line.height} role="img" aria-label={line.title} className="block tabular-nums">
        <text x={0} y={8} fontSize={10} fontWeight={600} letterSpacing="0.05em" fill="var(--ink-tertiary)">PB/AG MAX VS BALANCED</text>
        <line x1={line.x1} x2={line.x2} y1={line.zeroY} y2={line.zeroY} stroke="var(--ink-tertiary)" strokeWidth={1} />
        <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="var(--state-info)" strokeWidth={1.8} />
        {line.flip ? (
          <>
            <circle cx={line.flip.x} cy={line.zeroY} r={3} fill="var(--surface-2)" stroke="var(--ink-primary)" strokeWidth={1.5} />
            {line.flip.textVisible ? (
              <text x={line.flip.labelX} y={line.flip.labelY} fontSize={10} textAnchor="end" fill="var(--ink-secondary)">{line.flip.label}</text>
            ) : null}
          </>
        ) : null}
        <circle cx={line.today.x} cy={line.today.y} r={3.5} fill="var(--ink-primary)">
          <title>{line.today.label}</title>
        </circle>
      </svg>
      <div className="text-xs tabular-nums text-muted">{line.caption}</div>
    </div>
  );
}
